import type React from 'react';

import { Button } from '@change/ds-core-ui/Button';
import { HeroLayout } from '@change/ds-core-ui/HeroLayout';
import { Image } from '@change/ds-core-ui/Image';
import { Link } from '@change/ds-core-ui/Link';
import { Text } from '@change/ds-core-ui/Text';
import { ChevronLeftIcon } from '@change/ds-icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@change/ds-icons/ChevronRightIcon';

import { Translate } from '@change-corgi/core-react/i18n';

import { PetitionGuideResetCacheButton } from '#src/app/shared/components/petitionGuides';

import { ShareLinks } from './components/ShareLinks';
import { usePageHeading } from './hooks/usePageHeading';

export const PageHeading: React.FC<EmptyProps> = () => {
	const {
		data: { heading, heroImage, subheading, sapAiUrl },
		actions: { trackSapAiClick, trackSapClick },
	} = usePageHeading();

	return (
		<>
			<div className="border-stroke-weaker md:mb-11 md:border-b">
				<div className="container flex items-center py-4">
					<ChevronLeftIcon size="small" />
					<Link href="/petition-guides" variant="primary" data-qa="guides-landing-page-link" className="ml-2 shrink-0">
						<Translate value="corgi.petition_guides_article.page_heading.guides_link" />
					</Link>
					<ChevronRightIcon size="small" className="ml-2" />
					<Text className="ml-2 truncate" as="p">
						{heading}
					</Text>
				</div>
			</div>

			<PetitionGuideResetCacheButton />

			<div className="container">
				<HeroLayout
					className="pb-6 md:pb-11"
					media={
						heroImage && (
							<Image
								className="object-cover md:aspect-square md:rounded-md"
								alt={heroImage.title}
								src={heroImage.url}
							/>
						)
					}
					reverseMd
					fullWidthMediaSm
					variant="1/3"
				>
					<div className="bg-background-base shrink-0 grow-0 basis-1/2">
						<div className="flex h-full flex-col items-start gap-6 p-6 md:py-12 md:pl-0">
							<Text as="h1" variant="heading" size="x-large" className="m-0" data-qa="local-heading">
								{heading}
							</Text>
							<Text>{subheading}</Text>
							<ShareLinks />
						</div>
					</div>
				</HeroLayout>
			</div>

			<div className="bg-gradient-background-strong-3">
				<div className="container flex flex-col items-center justify-between gap-6 rounded-md py-4 text-center md:flex-row md:text-left">
					<Text fontWeight="bold" size="x-large" className="text-typography-inverse-strong-fixed grow">
						<Translate value="corgi.petition_guides_article.page_heading.start_a_petition.label" />
					</Text>

					<div className="flex w-full flex-col gap-4 md:items-center md:justify-end lg:flex-row">
						<Button
							data-qa="header-sap-button"
							className="w-full lg:w-auto"
							as="a"
							href="/start-a-petition/intro?source_location=petition_guides_article"
							variant="primary"
							size="large"
							onClick={trackSapClick}
						>
							<Translate value="corgi.petition_guides_article.page_heading.start_a_petition.button_label" />
						</Button>

						<Button
							data-qa="header-sap-ai-button"
							className="text-typography-inverse-strong-fixed w-full lg:w-auto"
							as="a"
							href={sapAiUrl}
							variant="secondary"
							size="large"
							onClick={trackSapAiClick}
						>
							<Translate value="corgi.petition_guides_article.page_heading.start_a_petition.button_label_ai" />
						</Button>
					</div>
				</div>
			</div>
		</>
	);
};
